import { yupResolver } from '@hookform/resolvers/yup'
import React, { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import Box from '../../Global/Box'
import { GET_ALL_PROCESSES } from '../../../graphql/Catalog/Processes/Processes'
import { toastSweetAlert } from '../../Helpers/ToastSweetAlert'
import InputController from '../../Global/InputController'
import * as Yup from 'yup'
import { useQuery } from '@apollo/client'
import { GET_ALL_PROCESSES_TYPES_BY_PROCESS_ID } from '../../../graphql/Catalog/Processes/ProcessesCategories'
import { GET_ALL_PROCESSES_SUBCATEGORIES_BY_PROCESS_CATEGORY_ID } from '../../../graphql/Catalog/Processes/ProcessesSubCategories'
import { useLoading } from '../../../LoadingOverlay/LoadingOverlayContext'
import { GET_ALL_PROCESSES_SUBCATEGORIES_CHILDREN_BY_PROCESS_SUBCATEGORY_ID } from '../../../graphql/Catalog/Processes/processesSubCategoriesChildren'

const FormProcesses = ({ refWizard, data, setData, setIsValidSteps }) => {
  //-------- Hooks ---------
  const [returnData, setReturnData] = useState(false)
  const { showLoading, hideLoading } = useLoading()

  const [processSelected, setProcessSelected] = useState(null)
  const [processCategorySelected, setProcessCategorySelected] = useState(null)
  const [processSubCategorySelected, setProcessSubCategorySelected] =
    useState(null)
  const [
    processSubCategorySelectedChildren,
    setProcessSubCategorySelectedChildren,
  ] = useState(null)

  const [options, setOptions] = useState({
    optionsProcesses: [],
    optionsProcessesCategories: [],
    optionsProcessesSubCategories: [],
    optionsProcessesSubCategoriesChildren: [],
  })

  //-------- Form ---------
  const validationSchema = Yup.object().shape({
    external_folio: Yup.string().required('El folio es requerido'),
    process_id: Yup.string().required('El proceso es requerido'),
    process_category_id: Yup.string()
      .required('La categoría es requerida')
      .typeError('La categoría es requerida'),
    process_sub_category_id: Yup.string()
      .required('La subcategoría es requerida')
      .typeError('La subcategoría es requerida'),
    process_sub_category_children_id: Yup.string().when([], {
      is: () => options.optionsProcessesSubCategoriesChildren.length > 0,
      then: Yup.string()
        .required('La subcategoría hija es requerida')
        .typeError('La subcategoría hija es requerida'),
      otherwise: Yup.string().nullable(),
    }),
    process_name: Yup.string(),
    category_name: Yup.string(),
    sub_category_name: Yup.string(),
    sub_category_children_name: Yup.string(),
  })

  const {
    handleSubmit,
    control,
    setValue,
    reset,
    methods,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
  })

  //-------- Queries ---------
  const {
    loading: loadingProcess,
    error: errorProcess,
    data: dataProcess,
  } = useQuery(GET_ALL_PROCESSES, {
    variables: {},
    fetchPolicy: 'no-cache',
  })

  const {
    loading: loadingProcessCategories,
    error: errorProcessCategories,
    data: dataProcessCategories,
  } = useQuery(GET_ALL_PROCESSES_TYPES_BY_PROCESS_ID, {
    variables: {
      processId: processSelected ? +processSelected.value : null,
    },
    fetchPolicy: 'no-cache',
    skip: !processSelected,
  })

  const {
    loading: loadingProcessSubCategories,
    error: errorProcessSubCategories,
    data: dataProcessSubCategories,
  } = useQuery(GET_ALL_PROCESSES_SUBCATEGORIES_BY_PROCESS_CATEGORY_ID, {
    variables: {
      processCategoryId: processCategorySelected
        ? +processCategorySelected.value
        : null,
    },
    fetchPolicy: 'no-cache',
    skip: !processCategorySelected,
  })

  const {
    loading: loadingProcessSubCategoriesChildren,
    error: errorProcessSubCategoriesChildren,
    data: dataProcessSubCategoriesChildren,
  } = useQuery(
    GET_ALL_PROCESSES_SUBCATEGORIES_CHILDREN_BY_PROCESS_SUBCATEGORY_ID,
    {
      variables: {
        processSubCategoryId: processSubCategorySelected?.value
          ? +processSubCategorySelected.value
          : null,
      },
      fetchPolicy: 'no-cache',
      skip: !processSubCategorySelected,
    }
  )

  //-------- Effects ---------
  useEffect(() => {
    if (returnData) return
    if (!processSelected) return
    reset({
      process_id: processSelected.value,
      process_category_id: '',
      process_sub_category_id: '',
      process_sub_category_children_id: '',
      process_name: '',
      category_name: '',
      sub_category_name: '',
      sub_category_children_name: '',
    })
    setOptions((prev) => ({
      ...prev,
      optionsProcessesCategories: [],
      optionsProcessesSubCategories: [],
    }))
    setProcessCategorySelected('')
    setProcessSubCategorySelected('')
    setData((prev) => ({
      ...prev,
      stepProcess: null,
    }))
    // set
  }, [processSelected])

  useEffect(() => {
    try {
      if (loadingProcess) return
      if (errorProcess)
        return toastSweetAlert({
          mode: 'error',
          message: errorProcess.message,
        })

      const infoProcesses = dataProcess.getAllProcesses.rows.map((process) => {
        return {
          label: process.name,
          value: process.id,
          key: process.key,
        }
      })

      setOptions((prev) => ({
        ...prev,
        optionsProcesses: infoProcesses,
      }))
    } catch (error) {
      return toastSweetAlert({
        mode: 'error',
        message: error.message,
      })
    }
  }, [dataProcess, loadingProcess, errorProcess])

  useEffect(() => {
    try {
      if (!processSelected) return
      if (loadingProcessCategories) return
      if (errorProcessCategories)
        throw new Error(errorProcessCategories.message)

      if (!dataProcessCategories.getAllProcessesCategoriesByProcessId)
        throw new Error(
          'No se encontraron categorías para el proceso seleccionado'
        )

      const optionsCategories =
        dataProcessCategories.getAllProcessesCategoriesByProcessId.map(
          (category) => {
            return {
              label: category.name,
              value: category.id,
            }
          }
        )

      setOptions((prev) => ({
        ...prev,
        optionsProcessesCategories: optionsCategories,
      }))
      setValue('process_name', processSelected.label)
    } catch (error) {
      return toastSweetAlert({
        mode: 'error',
        message: error.message,
      })
    }
  }, [dataProcessCategories, loadingProcessCategories, errorProcessCategories])

  useEffect(() => {
    try {
      if (!processCategorySelected) return
      if (loadingProcessSubCategories) return
      if (errorProcessSubCategories)
        throw new Error(errorProcessSubCategories.message)

      if (
        !dataProcessSubCategories.getAllProcessesSubCategoriesByProcessCategoryId
      )
        throw new Error(
          'No se encontraron subcategorías para la categoría seleccionada'
        )

      const optionsSubCategories =
        dataProcessSubCategories.getAllProcessesSubCategoriesByProcessCategoryId.map(
          (subCategory) => {
            return {
              label: subCategory.name,
              value: subCategory.id,
            }
          }
        )

      setOptions((prev) => ({
        ...prev,
        optionsProcessesSubCategories: optionsSubCategories,
      }))

      setValue('category_name', processCategorySelected.label)
    } catch (error) {
      return toastSweetAlert({
        mode: 'error',
        message: error.message,
      })
    }
  }, [
    dataProcessSubCategories,
    loadingProcessSubCategories,
    errorProcessSubCategories,
  ])

  useEffect(() => {
    if (!loadingProcessSubCategoriesChildren) {
      if (errorProcessSubCategoriesChildren) {
        return toastSweetAlert({
          mode: 'error',
          message: errorProcessSubCategoriesChildren.message,
        })
      }

      if (
        dataProcessSubCategoriesChildren?.getAllProcessesSubCategoriesChildrenByProcessSubCategoryId
      ) {
        const optionsSubCategoriesChildren =
          dataProcessSubCategoriesChildren?.getAllProcessesSubCategoriesChildrenByProcessSubCategoryId.map(
            (subCategory) => {
              return {
                label: subCategory.name,
                value: subCategory.id,
              }
            }
          )

        setOptions((prev) => ({
          ...prev,
          optionsProcessesSubCategoriesChildren: optionsSubCategoriesChildren,
        }))
        setValue('sub_category_name', processSubCategorySelected.label)
      }
    }
  }, [
    dataProcessSubCategoriesChildren,
    loadingProcessSubCategoriesChildren,
    errorProcessSubCategoriesChildren,
  ])

  useEffect(() => {
    if (data.stepProcess) {
      setReturnData(true)
      setProcessSelected({
        label: data.stepProcess.process_name,
        value: data.stepProcess.process_id,
      })
      setProcessCategorySelected({
        label: data.stepProcess.category_name,
        value: data.stepProcess.process_category_id,
      })

      setTimeout(() => {
        reset({
          external_folio: data.stepProcess.external_folio,
          process_id: data.stepProcess.process_id,
          process_category_id: data.stepProcess.process_category_id,
          process_sub_category_id: data.stepProcess.process_sub_category_id,
          process_sub_category_children_id:
            data.stepProcess.process_sub_category_children_id,
          process_name: data.stepProcess.process_name,
          category_name: data.stepProcess.category_name,
          sub_category_name: data.stepProcess.sub_category,
          sub_category_children_name:
            data.stepProcess.sub_category_children_name,
        })
        // setValue('process_id', data.stepProcess.process_id)
        // setValue('process_category_id', data.stepProcess.process_category_id)
        // setValue(
        //   'process_sub_category_id',
        //   data.stepProcess.process_sub_category_id
        // )
        // setValue('process_name', data.stepProcess.process_name)
        // setValue('category_name', data.stepProcess.category_name)
        // setValue('sub_category_name', data.stepProcess.sub_category_name)
        setReturnData(false)
      }, 300)
    }
  }, [data])

  //-------- Functions ---------
  const onSubmit = async (data) => {
    showLoading()
    try {
      // if (requiredPayment && !data.payment_id) {
      //   return toastSweetAlert({
      //     mode: 'warning',
      //     message: 'El pago es requerido',
      //   })
      // }

      setData((prev) => ({
        ...prev,
        stepProcess: {
          external_folio: data.external_folio,
          process_id: data.process_id,
          process_category_id: data.process_category_id,
          process_sub_category_id: data.process_sub_category_id,
          process_sub_category_children_id:
            data?.process_sub_category_children_id == 0
              ? null
              : data.process_sub_category_children_id,
          process_name: data.process_name,
          process_key: processSelected.key,
          category_name: data.category_name,
          sub_category_name: data.sub_category_name,
          sub_category_children_name: data.sub_category_children_name,
        },
      }))

      hideLoading()
      setIsValidSteps((prev) => ({
        ...prev,
        stepProcess: true,
      }))
      refWizard.current.nextTab()
    } catch (error) {
      hideLoading()
      return toastSweetAlert({
        mode: 'error',
        message: error.message,
      })
    }
  }

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box
            title="Datos del trámite"
            btnSubmitText={'Siguiente'}
            btnSubmit
            content={
              <>
                <div className="row">
                  <div className="col-lg-6">
                    <InputController
                      name="process_id"
                      label="Proceso"
                      inputType="choosen"
                      options={options.optionsProcesses}
                      placeholderTxt="Selecciona un proceso"
                      control={control}
                      changeAction={(e) => {
                        if (!e) return
                        setProcessSelected(e)

                        setProcessSubCategorySelected(null) //RESET CATEGORY
                        setValue('process_category_id', null)
                        setValue('category_name', '')
                        setOptions((prev) => ({
                          ...prev,
                          optionsProcessesSubCategoriesChildren: [],
                        }))
                      }}
                    />
                  </div>
                  <div className="col-lg-6">
                    <InputController
                      name="process_category_id"
                      label="Dependencias"
                      inputType="choosen"
                      options={options.optionsProcessesCategories}
                      placeholderTxt="Selecciona la dependencia"
                      control={control}
                      changeAction={(e) => {
                        if (!e) return
                        setProcessCategorySelected(e)

                        setProcessSubCategorySelected(null) //RESET SUBCATEGORY
                        setValue('process_sub_category_id', null)
                        setValue('sub_category_name', '')
                        setOptions((prev) => ({
                          ...prev,
                          optionsProcessesSubCategoriesChildren: [],
                        }))
                      }}
                      disabled={!processSelected}
                      valueManual={processCategorySelected}
                    />
                  </div>
                  <div className="col-lg-6">
                    <InputController
                      name="process_sub_category_id"
                      label="Tipo de trámite"
                      inputType="choosen"
                      options={options.optionsProcessesSubCategories}
                      placeholderTxt="Selecciona un tipo de trámite"
                      control={control}
                      disabled={!processCategorySelected}
                      changeAction={(e) => {
                        if (!e) return
                        setValue('sub_category_name', e.label)
                        setOptions((prev) => ({
                          ...prev,
                          optionsProcessesSubCategoriesChildren: [],
                        }))
                        setProcessSubCategorySelected(e)

                        setProcessSubCategorySelectedChildren(null) //RESET CHILDREN
                        setValue('process_sub_category_children_id', null)
                        setValue('sub_category_children_name', '')
                      }}
                      valueManual={processSubCategorySelected}
                    />
                  </div>
                  {options.optionsProcessesSubCategoriesChildren.length > 0 && (
                    <div className="col-lg-6">
                      <InputController
                        addClass="process_sub_category_children_input"
                        name="process_sub_category_children_id"
                        label="Subcategoría"
                        inputType="choosen"
                        options={options.optionsProcessesSubCategoriesChildren}
                        placeholderTxt="Selecciona una subcategoría"
                        control={control}
                        disabled={!processSubCategorySelected}
                        changeAction={(e) => {
                          if (!e) return
                          setValue('sub_category_children_name', e.label)
                          setProcessSubCategorySelectedChildren(e)
                        }}
                        valueManual={processSubCategorySelectedChildren}
                      />
                    </div>
                  )}
                  <div className="col-lg-6">
                    <InputController
                      name="external_folio"
                      label="Folio"
                      control={control}
                    />
                  </div>
                  <div className="hide">
                    <InputController
                      name="process_name"
                      control={control}
                      disabled={!processCategorySelected}
                    />
                  </div>
                  <div className="hide">
                    <InputController
                      name="category_name"
                      control={control}
                      disabled={!processCategorySelected}
                    />
                  </div>
                  <div className="hide">
                    <InputController
                      name="sub_category_name"
                      control={control}
                      disabled={!processCategorySelected}
                    />
                  </div>
                </div>
              </>
            }
          />
        </form>
      </FormProvider>
    </>
  )
}

export default FormProcesses
